import type { Collection, CollectionId, ContentItem } from '@kidshub/contracts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/api-client.js';
import { toErrorKey } from '../lib/error-code.js';
import { Chip } from '../ui/kit.js';

/**
 * Which collections this item is in.
 *
 * Separate from approval and deliberately so: approval decides whether a child
 * may see something at all, a collection decides where it sits on their shelf.
 * They use the same chips because both are answering "and where does this go",
 * but conflating them would let a parent believe that filing something away had
 * also given it to somebody.
 *
 * This exists on the card rather than only in the add flow because a library is
 * rearranged far more often than it is filled. Choosing a collection once, at
 * the moment of adding, and never being able to change it is the kind of gap
 * that looks like a missing feature to whoever hits it — which it was.
 */

interface CollectionChipsProps {
  item: ContentItem;
  collections: Collection[];
  onChanged: () => void;
}

export const CollectionChips = ({ item, collections, onChanged }: CollectionChipsProps) => {
  const { t } = useTranslation();
  const [pendingId, setPendingId] = useState<CollectionId | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  if (collections.length === 0) {
    return null;
  }

  const toggle = async (collection: Collection) => {
    setErrorKey(null);
    setPendingId(collection.id);

    const isIn = collection.itemIds.includes(item.id);
    const itemIds = isIn
      ? collection.itemIds.filter((id) => id !== item.id)
      : [...collection.itemIds, item.id];

    try {
      await apiClient.collections.setItems({ collectionId: collection.id, itemIds });
      onChanged();
    } catch (error) {
      setErrorKey(toErrorKey(error));
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="lib-field">
      <span className="t-label">{t('library.inCollections')}</span>

      <div className="chips">
        {collections.map((collection) => (
          <Chip
            key={collection.id}
            selected={collection.itemIds.includes(item.id)}
            disabled={pendingId !== null}
            onToggle={() => void toggle(collection)}
          >
            {collection.title}
          </Chip>
        ))}
      </div>

      {errorKey === null ? null : (
        <p className="help err" role="alert">
          {t(errorKey)}
        </p>
      )}
    </div>
  );
};
